import Swal from "sweetalert2";

/**
 * Merkezi Dosya Erişim Kontrolü Sistemi
 * Tüm proje genelinde döküman erişimi için kullanılır
 */

/**
 * Dosya erişim kontrolü yapar
 * @param {Object} document - Döküman objesi
 * @param {string} document.FilePath - Dosya yolu
 * @param {Function} onAccessDenied - Erişim reddedildiğinde çalışacak fonksiyon (opsiyonel)
 * @returns {boolean} - Erişim izni var mı?
 */
export const checkDocumentAccess = (document, onAccessDenied = null) => {
    if (!document || !document.FilePath) {
        if (onAccessDenied) {
            onAccessDenied();
        } else {
            showAccessDeniedAlert();
        }
        return false;
    }
    return true;
};

/**
 * Erişim reddedildi uyarısını gösterir
 */
export const showAccessDeniedAlert = () => {
    Swal.fire({
        title: 'Erişim Kısıtlı',
        text: 'Bu dosyaya erişim süresi geçmiştir. Adminle İletişime Geçiniz.',
        icon: 'warning',
        confirmButtonText: 'Tamam'
    });
};

/**
 * FilePath değerini geçerli bir URL'ye dönüştürür.
 * Her yol segmentini ayrı ayrı encode eder; böylece boşluk, parantez ve
 * Türkçe karakter (ç, ş, ğ, ı, ö, ü) içeren dosya adları doğru çözülür.
 * Ters bölü (\) ayıraçları da normalize edilir.
 * @param {string} filePath - Ham dosya yolu
 * @returns {string} - Güvenli, encode edilmiş URL
 */
export const buildSecureFileUrl = (filePath) => {
    if (!filePath) return filePath;

    return filePath
        .toString()
        .replace(/\\/g, '/')
        .split('/')
        .map((segment) => (segment === '' ? '' : encodeURIComponent(segment)))
        .join('/');
};

/**
 * Güvenli dosya indirme bağlantısı oluşturur
 * @param {Object} document - Döküman objesi
 * @param {string} fileName - Dosya adı (opsiyonel)
 * @param {Object} props - Ek props (style, className vs.)
 * @param {React.ReactNode} children - İçerik
 * @returns {React.ReactElement} - Güvenli indirme bağlantısı
 */
export const createSecureDownloadLink = (document, fileName = null, props = {}, children) => {
    const React = require('react');
    
    const handleClick = (e) => {
        if (!checkDocumentAccess(document)) {
            e.preventDefault();
            return false;
        }
    };

    if (!document || !document.FilePath) {
        return React.createElement('span', {
            ...props,
            style: { 
                ...props.style, 
                color: '#6c757d', 
                cursor: 'default',
                opacity: 0.5 
            },
            onClick: () => showAccessDeniedAlert(),
            title: 'Erişim Kısıtlı'
        }, children || (fileName || 'Erişim süresi geçmiştir'));
    }

    const downloadFileName = fileName || (document.FilePath ? 
        document.FilePath.split('_')[document.FilePath.split('_').length - 1] : 
        'download');

    return React.createElement('a', {
        ...props,
        href: buildSecureFileUrl(document.FilePath),
        download: downloadFileName,
        onClick: handleClick,
        style: { 
            ...props.style, 
            cursor: 'pointer' 
        }
    }, children || downloadFileName);
};

/**
 * Güvenli dosya gösterimi için wrapper bileşen
 * @param {Object} document - Döküman objesi
 * @param {string} fileName - Dosya adı (opsiyonel)
 * @param {Object} props - Ek props
 * @param {React.ReactNode} children - İçerik
 * @returns {React.ReactElement} - Güvenli dosya gösterimi
 */
export const SecureDocumentWrapper = ({ document, fileName = null, onClick = null, ...props }) => {
    const React = require('react');
    
    const handleClick = (e) => {
        if (!checkDocumentAccess(document)) {
            e.preventDefault();
            return false;
        }
        if (onClick) {
            onClick(e);
        }
    };

    const wrapperProps = {
        ...props,
        onClick: handleClick,
        style: { 
            ...props.style, 
            cursor: document && document.FilePath ? 'pointer' : 'default',
            opacity: document && document.FilePath ? 1 : 0.5
        }
    };

    if (!document || !document.FilePath) {
        wrapperProps.title = 'Erişim Kısıtlı';
    }

    return React.createElement('span', wrapperProps, props.children);
};

/**
 * Dosya adını temizler ve güvenli hale getirir
 * @param {string} filePath - Dosya yolu
 * @returns {string} - Temizlenmiş dosya adı
 */
export const getCleanFileName = (filePath) => {
    if (!filePath) return 'Erişim süresi geçmiştir';
    
    const parts = filePath.toString().split('/');
    const fileName = parts[parts.length - 1];
    
    if (fileName.includes('_')) {
        const nameParts = fileName.split('_');
        return nameParts[nameParts.length - 1];
    }
    
    return fileName;
};

/**
 * Döküman türüne göre filtreleme yapar
 * @param {Array} documents - Döküman listesi
 * @param {string} documentType - Döküman türü ("1", "2", "3")
 * @returns {Array} - Filtrelenmiş döküman listesi
 */
export const filterDocumentsByType = (documents, documentType) => {
    if (!documents || !Array.isArray(documents)) return [];
    
    return documents.filter(doc => 
        doc.Document && doc.Document.DocumentType === documentType
    );
};

/**
 * Toplu dosya erişim kontrolü yapar
 * @param {Array} documents - Döküman listesi
 * @returns {Object} - Erişilebilir ve erişilemeyen dosyalar
 */
export const bulkDocumentAccessCheck = (documents) => {
    if (!documents || !Array.isArray(documents)) {
        return { accessible: [], restricted: [] };
    }
    
    const accessible = [];
    const restricted = [];
    
    documents.forEach(doc => {
        if (checkDocumentAccess(doc, () => {})) {
            accessible.push(doc);
        } else {
            restricted.push(doc);
        }
    });
    
    return { accessible, restricted };
};

export default {
    checkDocumentAccess,
    showAccessDeniedAlert,
    buildSecureFileUrl,
    createSecureDownloadLink,
    SecureDocumentWrapper,
    getCleanFileName,
    filterDocumentsByType,
    bulkDocumentAccessCheck
};
